import { lazy, Suspense, useState, type MouseEvent } from 'react';
import DOMPurify from 'dompurify';
import Alert from 'components/Alert';
import If from 'components/If';
import LazySvg from 'components/LazySvg';
import Spin from 'components/Spin';
import { DISCOVER_PROPRIETARY_NOTIFICATION_MESSAGE } from 'services/misc/criticalNotificationConfig';
/* Reuses the parent's `.notification` + `.discoverNotification`
 * variant so the banner picks up the Angular `.discover-cc` styling
 * (light pink bg, red text, warning icon) instead of the solid-red
 * AntD default. */
import styles from '../index.module.scss';

const CardExpiryReminderModal = lazy(
  () => import('pages/dashboard/CardExpiryReminderModal'),
);

/**
 * Angular's custom-notification tier — client-synthesised banners that share
 * the critical-notification slot with the BE-supplied list
 * (`critical-notifications.component.html:11-38`).
 *
 * Today that is the Discover proprietary payment banner
 * (`checkProprietaryUser`, `critical-notifications.component.ts:126-138`).
 * Its visibility is decided by `CriticalNotification`, which owns the slot's
 * `If` gate and layout-shift key; this component owns the banner itself and
 * what its link does.
 *
 * Rendered as a sibling of the BE notifications inside the same
 * `styles.container` grid — every `.notification` occupies the same grid cell,
 * so a separate wrapper would change the layout.
 *
 * No close control — Angular's `.discover-cc` has none
 * (`critical-notifications.component.html:26-33`).
 */
function DiscoverUpdateCc() {
  const [isModalOpen, setIsModalOpen] = useState(false);

  /* Angular parity — `critical-notifications.component.html:30`
     `(click)="updatePayment()"` → `:186-197 dialog.open(...)`. The link opens
     the Card Expiry Reminder modal rather than navigating, so the anchor inside
     the sanitised HTML is intercepted.

     Opened straight from this component, as Angular opens its dialog, rather
     than through a store flag: `dialog.open` consults nothing, so a click always
     produces the modal even after the layout-level auto-open
     (`useCardExpiryReminder`) has been dismissed and left its session skip flag
     behind. The auto-open keeps working independently — this click no longer
     goes near it, so only ever one of the two is on screen. */
  const handleMessageClick = (event: MouseEvent<HTMLSpanElement>) => {
    const target = event.target as HTMLElement | null;
    if (!target?.closest('a')) return;

    event.preventDefault();
    setIsModalOpen(true);
  };

  return (
    <>
      {/* Wrap in the parent's `.container` so the nested
          `.container .notification` + `&.discoverNotification` selectors
          match — Angular's `.discover-cc` styling is scoped under the
          container in the parent SCSS. */}
      <div className={styles.container}>
        <Alert
          type='error'
          /* Angular parity — `.discover-cc` renders a warning triangle
           * just before the text. AntD renders it via `showIcon`; the
           * custom `icon` prop picks the local red warning SVG. */
          showIcon
          icon={
            <LazySvg
              name='attention-red'
              className={styles.discoverIcon}
              aria-hidden
            />
          }
          message={
            /* The interactive target inside is the anchor tag — it already handles
           keyboard activation (Enter) natively. This handler intercepts the
           click on that anchor to open a modal instead of navigating; the span
           itself isn't focusable or interactive. */
            /* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
            <span
              onClick={handleMessageClick}
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(
                  DISCOVER_PROPRIETARY_NOTIFICATION_MESSAGE,
                ),
              }}
            />
          }
          className={`${styles.notification} ${styles.discoverNotification}`}
        />
      </div>
      {/* Angular's `data: { showZuoraForm: true }` — the banner already carried
          the reason, so the modal opens on the Zuora payment step. */}
      <If condition={isModalOpen}>
        <Suspense fallback={<Spin fullscreen />}>
          <CardExpiryReminderModal
            isOpen
            skipToPayment
            onClose={() => setIsModalOpen(false)}
          />
        </Suspense>
      </If>
    </>
  );
}

export default DiscoverUpdateCc;
